import {
	createContext,
	useCallback,
	useContext,
	useMemo,
	useSyncExternalStore,
	type PropsWithChildren,
} from 'react'

import {
	type IntakeDraftState,
	type IntakeDraftStore,
} from '#app/routes/mocra+/_form+/intake/create-intake-draft-store.ts'
import {
	CURRENT_INTAKE_VERSION,
	type SectionKey,
} from '#app/routes/mocra+/_form+/intake/intake-draft.ts'
import { getIntakeDraftStore } from '#app/routes/mocra+/_form+/intake-storage.ts'

const DEFAULT_STATE: IntakeDraftState = {
	version: CURRENT_INTAKE_VERSION,
	sections: {},
	lastPersistedAt: null,
	errors: {},
}

type IntakeDraftContextValue = {
	readonly store: IntakeDraftStore
	readonly state: IntakeDraftState
}

const IntakeDraftContext = createContext<IntakeDraftContextValue | null>(null)

export function IntakeDraftProvider({ children }: PropsWithChildren) {
	const store = useMemo(() => getIntakeDraftStore(), [])

	const subscribe = useCallback(
		(listener: () => void) => store.subscribe(listener),
		[store],
	)

	const getSnapshot = useCallback(() => store.getState(), [store])

	const state = useSyncExternalStore(
		subscribe,
		getSnapshot,
		() => DEFAULT_STATE,
	)

	const value = useMemo<IntakeDraftContextValue>(
		() => ({ store, state }),
		[store, state],
	)

	return (
		<IntakeDraftContext.Provider value={value}>
			{children}
		</IntakeDraftContext.Provider>
	)
}

function useIntakeDraftContext(): IntakeDraftContextValue {
	const context = useContext(IntakeDraftContext)
	if (!context) {
		throw new Error(
			'useIntakeDraft hooks must be used within IntakeDraftProvider.',
		)
	}
	return context
}

export function useIntakeDraftState(): IntakeDraftState {
	return useIntakeDraftContext().state
}

export function useIntakeDraftStore(): IntakeDraftStore {
	return useIntakeDraftContext().store
}

export type SetSectionStateAction<Value> = Value | ((previous: Value) => Value)

export function useIntakeSection<Value>(
	section: SectionKey,
	defaultValue: Value,
): {
	readonly value: Value
	readonly hasStoredValue: boolean
	readonly setValue: (action: SetSectionStateAction<Value>) => void
	readonly replaceValue: (next: Value) => void
	readonly clearValue: () => void
} {
	const { store, state } = useIntakeDraftContext()
	const hasStoredValue = Object.prototype.hasOwnProperty.call(
		state.sections,
		section,
	)
	const current = hasStoredValue
		? (state.sections[section] as Value)
		: defaultValue

	const setValue = useCallback(
		(action: SetSectionStateAction<Value>) => {
			store.updateSection(section, (previous: unknown) => {
				const resolvedPrevious =
					previous === undefined ? defaultValue : (previous as Value)
				return typeof action === 'function'
					? (action as (value: Value) => Value)(resolvedPrevious)
					: action
			})
		},
		[defaultValue, section, store],
	)

	const replaceValue = useCallback(
		(next: Value) => {
			store.updateSection(section, next)
		},
		[section, store],
	)

	const clearValue = useCallback(() => {
		store.clearSection(section)
	}, [section, store])

	return {
		value: current,
		hasStoredValue,
		setValue,
		replaceValue,
		clearValue,
	}
}
